"use client";

import { useUmamiAnalytics } from "@analytics";
import { LocaleLink } from "@i18n/routing";
import { VideoTrendCards } from "@marketing/home/components/VideoTrendCards";
import {
	HOME_HERO_FALLBACK_MODEL,
	HOME_HERO_MODELS,
} from "@marketing/home/config/home-hero-models";
import { getModelDisplayName } from "@marketing/home/config/model-display-names";
import {
	ACCEPTED_REFERENCE_MEDIA_TYPES,
	ReferenceMediaThumb,
	getReferenceMediaKind,
} from "@saas/image-to-video/components/ReferenceMediaThumb";
import {
	type I2VReferenceMediaKind,
	useImageToVideo,
} from "@saas/image-to-video/hooks/use-image-to-video";
import type {
	I2VAspectRatio,
	I2VModelId,
	I2VResolution,
} from "@saas/image-to-video/types";
import {
	DEFAULT_I2V_MODEL,
	carryI2VModelToMode,
	collapseI2VModelFamilies,
	getBillableVideoSeconds,
	getI2VAspectRatios,
	getI2VCreditCost,
	getI2VDurationOptions,
	getI2VModel,
	getI2VModelFamily,
	getI2VModelPriceHint,
	getI2VModelsForMode,
	getI2VPromptMaxLength,
	supportsI2VReferenceAudio,
	supportsI2VReferenceVideo,
} from "@saas/image-to-video/types";
import { AspectRatioSwatch } from "@saas/shared/components/AspectRatioSwatch";
import type { VideoHistoryItem } from "@saas/shared/history/types";
import { useIncomingVideoAction } from "@saas/shared/history/video-action-handoff";
import { Button } from "@ui/components/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
} from "@ui/components/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@ui/components/tabs";
import { cn } from "@ui/lib";
import {
	CheckIcon,
	CircleCheckIcon,
	CrownIcon,
	DownloadIcon,
	ImagesIcon,
	LayersIcon,
	Loader2Icon,
	RefreshCwIcon,
	WandSparklesIcon,
	ZapIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
	ACCEPTED_IMAGE_TYPES,
	Chip,
	ResultPlaceholder,
	UploadBox,
	VIDEO_HERO_EXAMPLE_EVENT,
	VIDEO_HERO_ID,
	VIDEO_HERO_IMAGES_EVENT,
	VIDEO_HERO_MODE_EVENT,
	VIDEO_HERO_PROMPT_ID,
	VIDEO_HERO_TEMPLATE_EVENT,
} from "./HeroControls";

// 常量搬到 HeroControls（生图页的 hero 也用，免得为几个字符串把整个视频 hero 打进包里），
// 这里转出一份，老的 import 路径不用改
export {
	VIDEO_HERO_EXAMPLE_EVENT,
	VIDEO_HERO_ID,
	VIDEO_HERO_IMAGES_EVENT,
	VIDEO_HERO_MODE_EVENT,
	VIDEO_HERO_PROMPT_ID,
	VIDEO_HERO_TEMPLATE_EVENT,
};

// 付费墙只有撞墙才用得上，按需加载，别进首页首包
const CreditsPaywallModal = dynamic(
	() =>
		import("@saas/payments/components/CreditsPaywallModal").then(
			(mod) => mod.CreditsPaywallModal,
		),
	{ ssr: false },
);

export interface HeroShowcaseExample {
	mode: HeroMode;
	model: I2VModelId;
	prompt: string;
	images: string[];
	aspectRatio: I2VAspectRatio;
	/** prompt 模板带时长；样片不传，保留用户当前选的 */
	duration?: number;
	/** 参考视频 / 音频，地址要在 NEXT_PUBLIC_R2_PUBLIC_URL 下（后端只收那里的） */
	referenceMedia?: {
		kind: I2VReferenceMediaKind;
		url: string;
		durationSec: number;
	}[];
}

export interface HeroPresetTemplate {
	id: string;
	label: string;
	prompt: string;
	/** 按画质档 id 换一套提示词（丧尸页 Lite 走 Grok、Standard/HD 走 Wan），没写的档用 prompt */
	promptByQuality?: Record<string, string>;
	/**
	 * 模板固定带的一段参考视频（Burning Bridges 的 Me：原 MV 转的深度图，只给动作）。只在当前档的
	 * 模型收参考视频时带上（Grok 不收），按（视频秒数 + 生成秒数）计价，比例锁成 aspectRatio。
	 * 地址必须在 NEXT_PUBLIC_R2_PUBLIC_URL 下：后端只收自己 R2 上的参考视频
	 */
	referenceVideo?: {
		url: string;
		durationSec: number;
		aspectRatio: I2VAspectRatio;
	};
	/**
	 * 按画质档 id 换一段参考视频（Ozil Goal：Lite 只挂挑门将那 2 秒，进免费额度），没写的档用 referenceVideo。
	 * Studio 特效工作台（use-preset-video-form.ts）和营销 hero（Rumpelstiltskin）都认
	 */
	referenceVideoByQuality?: Record<
		string,
		{ url: string; durationSec: number; aspectRatio: I2VAspectRatio }
	>;
	/**
	 * 选填槽（requiredSlots 之后的）全空时整套换成这里的写法和参考视频，不回落到上面那套
	 * （Rumpelstiltskin：只传主角时少女留原片的样子，Standard 挂只给小矮人打码的原片）
	 */
	optionalEmpty?: Pick<
		HeroPresetTemplate,
		| "prompt"
		| "promptByQuality"
		| "referenceVideo"
		| "referenceVideoByQuality"
	>;
	/**
	 * 用户自己传参考视频的模板（AI Dance 的 Your own dance，2026-11-01）：不带固定参考视频，
	 * Studio 特效工作台在照片下面出一个视频上传格，最长 maxSeconds（超长截前面），
	 * 只有 qualityIds 里的档能选。营销 hero 不认
	 */
	userVideo?: { maxSeconds: number; qualityIds: readonly string[] };
}

/** 一档画质 = 模型 + 分辨率；label / hint 已翻译 */
export interface HeroPresetQuality {
	id: string;
	label: string;
	hint: string;
	model: I2VModelId;
	resolution: I2VResolution;
	/** 这一档固定的时长，写了时长分段就只剩这一个；不写用 durationOptions */
	duration?: number;
	/** 选中这一档时露在档位下面的一行说明（Bulin 47：Lite 不是原片，想要原片选 Standard / HD） */
	note?: string;
	/** 这一档出片后结果卡里的升档引导：点按钮切到 targetId 那档并滚回输入卡，不自动生成 */
	upgrade?: { text: string; cta: string; targetId: string };
}

function getTemplatePrompt(
	template: HeroPresetTemplate,
	qualityId: string | undefined,
	optionalEmpty = false,
): string {
	const variant = (optionalEmpty && template.optionalEmpty) || template;
	return (
		(qualityId && variant.promptByQuality?.[qualityId]) || variant.prompt
	);
}

/** 模板在这一档固定带的参考视频：按档换的优先，没有就用模板的那段（同 use-preset-video-form.ts） */
function getTemplateVideo(
	template: HeroPresetTemplate | undefined,
	qualityId: string | undefined,
	optionalEmpty = false,
) {
	const variant = (optionalEmpty && template?.optionalEmpty) || template;
	return (
		(qualityId && variant?.referenceVideoByQuality?.[qualityId]) ||
		variant?.referenceVideo
	);
}

/**
 * 预设变体（如 /hotel-lobby-ai-video-generator）：锁定 reference 模式 + 固定槽位，
 * 模式 tab 换成模板 chip，模型 / 分辨率收成几档画质，提示词框换成 director's note。
 * 传了它 hero 就不再是首页那套三 tab 表单
 */
export interface HeroPreset {
	mode: "reference";
	qualities: HeroPresetQuality[];
	defaultQualityId: string;
	aspectRatioOptions: readonly I2VAspectRatio[];
	defaultAspectRatio: I2VAspectRatio;
	durationOptions: readonly number[];
	defaultDuration: number;
	pendingStateKey: string;
	/** 已翻译的槽位标签，长度就是槽位数 */
	slotLabels: string[];
	/**
	 * 按槽位下标给默认图 URL，没有的写 null（Rumpelstiltskin 的少女槽）。空槽自动填回默认图，
	 * 默认图上不给删除、只给「换一张」；换上的图删掉又回到默认图。不传就是普通必填槽
	 */
	slotDefaults?: (string | null)[];
	/**
	 * 默认图锁死，不出「换一张」（Bulin 47：场景图是给 Grok 认比例、重建现场的内部手段，
	 * 竞品都只收一张照片）
	 */
	lockSlotDefaults?: boolean;
	/** 前几个槽必填（AI Kiss：合照一张就能出，第二槽选填）；不传每个槽都要填 */
	requiredSlots?: number;
	/**
	 * 模板挂着参考视频时只把前几个槽的图发给模型（Bulin 47：默认场景图只给 Lite 用，视频档带着它
	 * H3 会拿它拍一个开场镜头再切进原片）。不传就是全部槽
	 */
	referenceVideoImageSlots?: number;
	templates: HeroPresetTemplate[];
	/** 模板 + 用户 note 拼成最终 prompt；note 为空时原样返回模板 */
	buildPrompt: (templatePrompt: string, note: string) => string;
	/** 拼接时前缀的长度，算 note 还能写几个字用 */
	notePrefixLength: number;
	copy: {
		title: string;
		subtitle: string;
		badges: [string, string, string, string];
		freeCredits: string;
		templatesLabel: string;
		qualityLabel: string;
		ratioLabel: string;
		durationLabel: string;
		noteLabel: string;
		notePlaceholder: string;
		/** 默认图上「换一张」按钮的文案，配 slotDefaults 用 */
		swapLabel?: string;
		/** 出片后互推区的标题；特效页写「More AI video effects」，不传用热梗页那句 */
		moreLinks?: string;
		/** 配 userVideo 模板：视频上传格的标题和空格里的说明 */
		userVideoLabel?: string;
		userVideoHint?: string;
		trust: [string, string, string];
	};
}

/**
 * 模型落地页变体（如 /seedance-2-ai-video-generator）：三个模式 tab
 * 和首页一样照常可切，只是首屏默认停在某个 tab、选中某个模型和分辨率，标题区换文案。
 * 切 tab 时 hook 的 carryI2VModelToMode 会尽量留在同一档位，不会掉回 Basic。和 preset 互斥
 */
export interface HeroModelPreset {
	/** 首屏停在哪个 tab，不传是合并 tab 的文生状态 */
	mode?: HeroMode;
	model: I2VModelId;
	resolution?: I2VResolution;
	/** 首屏时长，不传用模型自己的默认；切模型后回到新模型的默认 */
	duration?: number;
	/** 模型下拉只列这几个（按各 tab 支持的过滤），不传就是公共下拉 */
	models?: readonly I2VModelId[];
	/** Seedance 2.5 落地页提供音频开关；其余页面保持原样 */
	showAudioToggle?: boolean;
	/** 登录前现场的 IndexedDB key，别和首页 / /image-to-video 串 */
	pendingStateKey: string;
	copy: Pick<
		HeroPreset["copy"],
		"title" | "subtitle" | "badges" | "freeCredits" | "trust"
	>;
}

const MAX_IMAGE_SIZE_MB = 30;

/** hero 接得住的 hook mode（事件、样片、模式卡都用这个） */
const HERO_MODES = ["text", "start", "between", "reference"] as const;
export type HeroMode = (typeof HERO_MODES)[number];

/**
 * 三个 tab：文生 / 单图合成一个（"start"，上传框可选，没图走 text、有图走 start），
 * 首尾帧，参考图。2026-10-04 老公定
 */
const HERO_TABS = ["start", "between", "reference"] as const;
type HeroTab = (typeof HERO_TABS)[number];
const tabOf = (mode: HeroMode): HeroTab => (mode === "text" ? "start" : mode);
const modesOfTab = (tab: HeroTab): readonly HeroMode[] =>
	tab === "start" ? ["text", "start"] : [tab];

type GenerationPhase = "upload" | "submit" | "generate" | "done";
const HERO_TAB_ICONS = {
	start: WandSparklesIcon,
	between: LayersIcon,
	reference: ImagesIcon,
} as const;

/**
 * 首页 hero 生成器，布局照 imgveo 首页：居中标题 + 模式 tab + 一张横向输入卡。
 * 逻辑全部复用 /image-to-video 的 useImageToVideo（游客点 Create 存表单、弹登录、
 * 登录回来恢复，都在 hook 里）。这里只有展示层，不带删除 / 收藏 / 素材库，
 * 所以不依赖营销 layout 没挂的 ConfirmationAlertProvider。
 */
export function VideoGeneratorHero({
	preset: rawPreset,
	modelPreset,
}: {
	preset?: HeroPreset;
	modelPreset?: HeroModelPreset;
} = {}) {
	// 发版瞬间可能混到上一版的调用方 chunk，传来的 preset 缺新字段（10/1 线上撞过：
	// 旧 HotelLobbyHero 没有 qualities，整页崩到错误边界，刷新即好）。
	// 这时先按无预设渲染不崩，再替用户刷新一次；sessionStorage 防死循环
	const isStalePreset =
		rawPreset !== undefined && !Array.isArray(rawPreset.qualities);
	const preset = isStalePreset ? undefined : rawPreset;
	const copy = preset?.copy ?? modelPreset?.copy;
	useEffect(() => {
		if (!isStalePreset) {
			return;
		}
		const key = "video-hero:stale-preset-reload";
		try {
			if (sessionStorage.getItem(key)) {
				return;
			}
			sessionStorage.setItem(key, "1");
		} catch {
			return;
		}
		window.location.reload();
	}, [isStalePreset]);
	const t = useTranslations("home.video.hero");
	const tI2V = useTranslations("imageToVideo.client");
	const { trackEvent } = useUmamiAnalytics();
	const defaultQuality = preset?.qualities.find(
		(item) => item.id === preset.defaultQualityId,
	);
	// 比例在本地记一份：Grok 要两张图齐了才让挑比例（aspectRatioMinImages），
	// 没齐时 hook 会把它改成 auto，图齐了再按本地这份写回去
	const [presetRatio, setPresetRatio] = useState<I2VAspectRatio>(
		preset?.defaultAspectRatio ?? "auto",
	);
	const {
		mode,
		setMode,
		setExample,
		model,
		setModel,
		modelConfig,
		inputImages,
		filledImages,
		maxImages,
		hasRequiredImages,
		setImageAt,
		addImageFiles,
		removeImage,
		referenceMedia,
		referenceVideos,
		referenceAudios,
		addReferenceMedia,
		removeReferenceMedia,
		restoreReferenceMedia,
		videoLimits,
		audioLimits,
		referenceVideoSeconds,
		isProcessingMedia,
		prompt,
		setPrompt,
		resolution,
		setResolution,
		lockedResolutions,
		aspectRatio,
		canPickAspectRatio,
		setAspectRatio,
		duration,
		setDuration,
		generateAudio,
		setGenerateAudio,
		creditCost,
		isPending,
		isUploading,
		handleGenerate,
		applyHistoryItem,
		latestStartedBatchId,
		resumedBatchId,
		history,
		paywallReason,
		setPaywallReason,
	} = useImageToVideo(
		"all",
		preset && defaultQuality
			? {
					initial: {
						mode: preset.mode,
						model: defaultQuality.model,
						resolution: defaultQuality.resolution,
						aspectRatio: preset.defaultAspectRatio,
						duration:
							defaultQuality.duration ?? preset.defaultDuration,
						// 首屏还没传图，有选填槽的页就是 optionalEmpty 那套
						prompt:
							preset.templates[0] &&
							getTemplatePrompt(
								preset.templates[0],
								defaultQuality.id,
								preset.requiredSlots !== undefined,
							),
					},
					referenceSlots: preset.slotLabels.length,
					requiredReferenceSlots: preset.requiredSlots,
					referenceVideoImageSlots: preset.referenceVideoImageSlots,
					pendingStateKey: preset.pendingStateKey,
				}
			: modelPreset
				? {
						allowTextMode: true,
						initial: {
							mode: modelPreset.mode ?? "text",
							model: modelPreset.model,
							resolution: modelPreset.resolution,
							duration: modelPreset.duration,
						},
						pendingStateKey: modelPreset.pendingStateKey,
					}
				: {
						allowTextMode: true,
						initial: {
							mode: "text",
							model: DEFAULT_I2V_MODEL.text,
						},
					},
	);

	// 限定模型的页（落地页和首页都是）：表单里的模型不在白名单时（恢复出旧现场、
	// Generate again 回填了别处生成的批次、切 tab 被 carry 到 basic-between）拉回本页
	// 默认。Generate again 本身照旧按那一批的模型和价格原样重跑（effect 在同一轮先
	// 拿到旧值），这里只管之后表单显示什么。预设页（hotel lobby）不限模型
	const allowedModels =
		modelPreset?.models ?? (preset ? undefined : HOME_HERO_MODELS);
	const fallbackModel = modelPreset?.model ?? HOME_HERO_FALLBACK_MODEL[mode];
	useEffect(() => {
		if (allowedModels && !allowedModels.includes(model)) {
			setModel(fallbackModel);
		}
	}, [allowedModels, fallbackModel, model, setModel]);

	// 名单里没有模型支持的 tab 不出（Seedance 页四档都不支持参考图，只剩两个 tab）
	const visibleTabs = HERO_TABS.filter((tab) =>
		modesOfTab(tab).some((tabMode) =>
			(allowedModels ?? HOME_HERO_MODELS).some((id) =>
				getI2VModel(id).modes.includes(tabMode),
			),
		),
	);
	const isModeVisible = visibleTabs.includes(tabOf(mode));
	// 合并 tab 里「单图但没图」等于文生：恢复现场、Try it 切到 start、样片清图后都落回 text，
	// 否则只写 prompt 的用户会卡在 Create 置灰。恢复出本页不出的 tab（如 Seedance 页
	// 拿到别处存的 reference）也落回 text。预设页不管
	useEffect(() => {
		if (preset) {
			return;
		}
		if (!isModeVisible || (mode === "start" && !inputImages[0])) {
			setMode("text");
		}
	}, [preset, isModeVisible, mode, inputImages, setMode]);

	// 预设页：hook 的 prompt 就是「模板 + note」拼好的结果，模板和 note 每次从它反推，
	// 登录回来恢复现场时两者自然对上，不另存 state
	// 模板可能按画质档、按选填槽空不空换了写法，每条模板的每种写法都要认得出来
	const activeMatch = preset?.templates
		.flatMap((item) =>
			[item, ...(item.optionalEmpty ? [item.optionalEmpty] : [])]
				.flatMap((variant) => [
					variant.prompt,
					...Object.values(variant.promptByQuality ?? {}),
				])
				.map((text) => ({ template: item, text })),
		)
		.find(
			({ text }) =>
				prompt === text || prompt.startsWith(text.concat("\n")),
		);
	const activeTemplate = activeMatch?.template;
	const note =
		preset && activeMatch
			? prompt.slice(activeMatch.text.length + preset.notePrefixLength)
			: "";
	// 认档先看「模型 + 分辨率 + 固定时长」：Rumpelstiltskin 的 Lite 和 Standard 都是 Mini 480p，只差 4 / 9 秒。
	// 对不上固定时长的（历史行回填了别的秒数）再按模型 + 分辨率认第一档，下面的锁会把时长改回去
	const sameModel = (item: HeroPresetQuality) =>
		item.model === model && item.resolution === resolution;
	const activeQuality =
		preset?.qualities.find(
			(item) => sameModel(item) && item.duration === duration,
		) ?? preset?.qualities.find(sameModel);
	// 选填槽全空（Rumpelstiltskin 只传了主角）：模板换 optionalEmpty 那套写法和参考视频
	const optionalEmpty =
		preset?.requiredSlots !== undefined &&
		inputImages.slice(preset.requiredSlots).every((image) => !image);
	// 当前画质档该用的那套模板写法；和 activeMatch.text 不一致时同步 effect 会换过去
	const activeTemplatePrompt = activeTemplate
		? getTemplatePrompt(activeTemplate, activeQuality?.id, optionalEmpty)
		: undefined;
	// 模板固定带的参考视频：这一档的模型收视频才带（videoLimits 只在收的时候有值）
	const presetVideo = videoLimits
		? getTemplateVideo(activeTemplate, activeQuality?.id, optionalEmpty)
		: undefined;
	const lockedRatio = presetVideo?.aspectRatio;
	// 时长分段：这一档固定了时长就只剩它一个
	const presetDurations = activeQuality?.duration
		? [activeQuality.duration]
		: (preset?.durationOptions ?? []);
	const selectQuality = (quality: HeroPresetQuality) => {
		if (lockedResolutions.has(quality.resolution)) {
			setPaywallReason("video-resolution");
			return;
		}
		setModel(quality.model);
		setResolution(quality.resolution);
		// 同模型同分辨率的两档只差时长（Rumpelstiltskin Lite / Standard），不顺手设时长就切不过去
		if (quality.duration) {
			setDuration(quality.duration);
		}
		// Lite 没传齐两张图时 hook 里的比例是 auto，setModel 会把它夹成 Master 的第一个
		// 合法值 16:9，再被下面的同步 effect 反写进 presetRatio。这里按本地记的比例写回，
		// 同一批更新里后写的赢
		setAspectRatio(presetRatio);
	};
	const selectRatio = (ratio: I2VAspectRatio) => {
		setPresetRatio(ratio);
		if (canPickAspectRatio) {
			setAspectRatio(ratio);
		}
	};

	// 预设页的锁：有独立 pendingStateKey 后正常不会偏，防的是历史行回填（Generate again）
	// 和旧版模板（prompt 对不上任何一条）。档位闸门把 HD 拉回 480p 时正好落到 Standard
	useEffect(() => {
		if (!preset) {
			return;
		}
		if (mode !== preset.mode) {
			setMode(preset.mode);
		} else if (!activeQuality && defaultQuality) {
			setModel(defaultQuality.model);
			setResolution(defaultQuality.resolution);
		} else if (
			activeQuality?.duration
				? duration !== activeQuality.duration
				: !preset.durationOptions.includes(duration)
		) {
			setDuration(activeQuality?.duration ?? preset.defaultDuration);
		} else if (!activeTemplate && preset.templates[0]) {
			setPrompt(
				getTemplatePrompt(
					preset.templates[0],
					activeQuality?.id,
					optionalEmpty,
				),
			);
		} else if (
			activeTemplatePrompt !== undefined &&
			activeMatch?.text !== activeTemplatePrompt
		) {
			// 换了画质档或填 / 清了选填槽：模板换成对应的写法，note 原样带过去
			setPrompt(preset.buildPrompt(activeTemplatePrompt, note));
		}
	}, [
		preset,
		mode,
		activeQuality,
		defaultQuality,
		optionalEmpty,
		activeTemplate,
		activeMatch?.text,
		activeTemplatePrompt,
		note,
		duration,
		setMode,
		setModel,
		setResolution,
		setDuration,
		setPrompt,
	]);

	// 比例：hook 在 Grok 没齐两张图时会改成 auto，图齐了按本地这份写回；
	// 用户点选或恢复现场带进来的合法值同步回本地。模板带参考视频时锁成它的比例，
	// 不写进本地这份，解锁时按本地这份写回（不然用户选的 9:16 会被锁定的 16:9 顶掉）
	const wasRatioLocked = useRef(false);
	useEffect(() => {
		if (!preset || !canPickAspectRatio) {
			return;
		}
		if (lockedRatio) {
			wasRatioLocked.current = true;
			if (aspectRatio !== lockedRatio) {
				setAspectRatio(lockedRatio);
			}
		} else if (wasRatioLocked.current) {
			wasRatioLocked.current = false;
			setAspectRatio(presetRatio);
		} else if (preset.aspectRatioOptions.includes(aspectRatio)) {
			setPresetRatio(aspectRatio);
		} else {
			setAspectRatio(presetRatio);
		}
	}, [
		preset,
		canPickAspectRatio,
		lockedRatio,
		aspectRatio,
		presetRatio,
		setAspectRatio,
	]);

	// 固定参考视频跟着模板和档位走：该带时表单里只留这一段，不该带时清空。
	// 预设页没有视频上传格，用户自己加不进来
	const presetVideoUrl = presetVideo?.url;
	const presetVideoDuration = presetVideo?.durationSec ?? 0;
	useEffect(() => {
		if (!preset) {
			return;
		}
		if (presetVideoUrl) {
			if (
				referenceMedia.length !== 1 ||
				referenceMedia[0].url !== presetVideoUrl
			) {
				restoreReferenceMedia([
					{
						kind: "video",
						url: presetVideoUrl,
						durationSec: presetVideoDuration,
					},
				]);
			}
		} else if (referenceMedia.length) {
			restoreReferenceMedia([]);
		}
	}, [
		preset,
		presetVideoUrl,
		presetVideoDuration,
		referenceMedia,
		restoreReferenceMedia,
	]);

	useEffect(() => {
		if (preset) {
			return;
		}
		const onSetMode = (event: Event) => {
			const next = (event as CustomEvent<HeroMode>).detail;
			if (HERO_MODES.includes(next)) {
				setMode(next);
			}
		};
		window.addEventListener(VIDEO_HERO_MODE_EVENT, onSetMode);
		return () =>
			window.removeEventListener(VIDEO_HERO_MODE_EVENT, onSetMode);
	}, [preset, setMode]);

	// 样片的原图是 assets 域的公开 URL：只填 previewUrl、不带 file，生成时原样交给后端，
	// 不用在浏览器里下载（那个域没开 CORS）。登录前现场也按 url 存，见 requestLogin。
	// 预设页按它的固定槽位填；模型落地页（首尾帧页的「Use these images」）是首尾两槽：
	// 不在首尾帧 tab 时先切过去，setMode 的清槽 updater 排在 setImageAt 前面，图不会被清掉
	const slotCount = preset ? preset.slotLabels.length : 2;
	useEffect(() => {
		if (!preset && !modelPreset) {
			return;
		}
		const onSetImages = (event: Event) => {
			const urls = (event as CustomEvent<string[]>).detail;
			if (!preset && mode !== "between") {
				setMode("between");
			}
			urls.slice(0, slotCount).forEach((url, index) => {
				setImageAt(index, { previewUrl: url });
			});
			// 预设页样片给的图比槽少（AI Kiss 的单张合照）时，把多出来的槽清掉，
			// 免得上次留在第二槽的人跟着合照一起送给模型
			if (preset) {
				for (let index = urls.length; index < slotCount; index++) {
					setImageAt(index, null);
				}
			}
		};
		window.addEventListener(VIDEO_HERO_IMAGES_EVENT, onSetImages);
		return () =>
			window.removeEventListener(VIDEO_HERO_IMAGES_EVENT, onSetImages);
	}, [preset, modelPreset, mode, slotCount, setMode, setImageAt]);

	// 带默认图的槽：首屏、删掉换上的图、恢复现场后空着时都填回默认图，同样只填 previewUrl。
	// 等 mode 先被锁回预设的 reference，别往别的模式的槽里填
	const slotDefaults = preset?.slotDefaults;
	useEffect(() => {
		if (!slotDefaults || mode !== preset?.mode) {
			return;
		}
		inputImages.forEach((image, index) => {
			const url = slotDefaults[index];
			if (!image && url) {
				setImageAt(index, { previewUrl: url }, true);
			}
		});
	}, [slotDefaults, preset?.mode, mode, inputImages, setImageAt]);

	// 样片的「Try this example」先切模板再填图：note 保留，只换模板部分
	useEffect(() => {
		if (!preset) {
			return;
		}
		const onSetTemplate = (event: Event) => {
			const id = (event as CustomEvent<string>).detail;
			const template = preset.templates.find((item) => item.id === id);
			if (template) {
				setPrompt(
					preset.buildPrompt(
						getTemplatePrompt(
							template,
							activeQuality?.id,
							optionalEmpty,
						),
						note,
					),
				);
			}
		};
		window.addEventListener(VIDEO_HERO_TEMPLATE_EVENT, onSetTemplate);
		return () =>
			window.removeEventListener(
				VIDEO_HERO_TEMPLATE_EVENT,
				onSetTemplate,
			);
	}, [preset, note, activeQuality?.id, optionalEmpty, setPrompt]);

	const [uploadNudge, setUploadNudge] = useState(false);
	useEffect(() => {
		if (!modelPreset) {
			return;
		}
		const onSetExample = (event: Event) => {
			const example = (event as CustomEvent<HeroShowcaseExample>).detail;
			if (!example || !HERO_MODES.includes(example.mode)) {
				return;
			}
			setExample(example);
			// 图生模板（如 2.5 页的 Photo to life）不带图：合并 tab 没图会落回文生，
			// 高亮上传框提醒用户传自己的照片，传了自然切到 start
			setUploadNudge(
				example.mode === "start" && example.images.length === 0,
			);
		};
		window.addEventListener(VIDEO_HERO_EXAMPLE_EVENT, onSetExample);
		return () =>
			window.removeEventListener(VIDEO_HERO_EXAMPLE_EVENT, onSetExample);
	}, [modelPreset, setExample]);

	// 点 Create 后把结果区滚进视口：手机上结果区在输入卡下面整屏之外，桌面上也常在
	// 首屏外。只在用户点过按钮且任务真的起来（isPending 变 true）时滚，登录/付费墙
	// 拦下的点击和刷新后接手轮询的 isPending 都不滚
	const resultsRef = useRef<HTMLDivElement>(null);
	// 结果卡的升档按钮切完档滚回输入卡，让人看到新档的价格和 Create
	const composerRef = useRef<HTMLDivElement>(null);
	const scrollToResultsOnStart = useRef(false);
	useEffect(() => {
		if (isPending && scrollToResultsOnStart.current) {
			scrollToResultsOnStart.current = false;
			resultsRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}
	}, [isPending]);
	const startGeneration = () => {
		scrollToResultsOnStart.current = true;
		void handleGenerate();
	};

	// Generate again = 按那一批的参数原样再跑一次（图、提示词、模型、分辨率、比例、时长），
	// 用户中途改过表单也不影响。applyHistoryItem 设完表单要等下一轮渲染 handleGenerate
	// 才看得到新值，所以走一个 requested 标记，和 /image-to-video 的 Regenerate 同一套
	const [regenerateRequested, setRegenerateRequested] = useState(false);
	useEffect(() => {
		if (!regenerateRequested) {
			return;
		}
		setRegenerateRequested(false);
		scrollToResultsOnStart.current = true;
		void handleGenerate();
	}, [regenerateRequested, handleGenerate]);

	// 热梗页出的片在别的页面（Hotel Lobby、/image-to-video、My Assets）点了 Reprompt / Regenerate，
	// 带着整条记录跳回来（video-home.ts）。首页和模型落地页不是谁的出生页，不接
	useIncomingVideoAction((item, action) => {
		applyHistoryItem(item);
		if (action === "regenerate") {
			setRegenerateRequested(true);
		} else {
			composerRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}
	}, Boolean(preset));

	const acceptFile = (file: File | undefined): File | null => {
		if (!file) {
			return null;
		}
		if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
			toast.error(tI2V("invalidImageType"));
			return null;
		}
		if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
			toast.error(tI2V("imageTooLarge", { size: MAX_IMAGE_SIZE_MB }));
			return null;
		}
		return file;
	};

	const handleSlotFile = (index: number, file: File | undefined) => {
		const accepted = acceptFile(file);
		if (accepted) {
			setImageAt(index, {
				previewUrl: URL.createObjectURL(accepted),
				file: accepted,
			});
		}
	};

	// 合并 tab 文生状态下传图 = 切到单图。setMode 先把槽位清成 [null]，setImageAt 的
	// updater 排在后面执行，图不会被清掉。比例回到跟图走的 auto（文生是 16:9）
	const handleTextModeFile = (file: File | undefined) => {
		const accepted = acceptFile(file);
		if (!accepted) {
			return;
		}
		setMode("start");
		setImageAt(0, {
			previewUrl: URL.createObjectURL(accepted),
			file: accepted,
		});
		if (
			getI2VAspectRatios(
				carryI2VModelToMode(model, "start"),
				"start",
			).includes("auto")
		) {
			setAspectRatio("auto");
		}
	};

	const handleReferenceFiles = (files: File[]) => {
		const media = files.flatMap((file) => {
			const kind = getReferenceMediaKind(file);
			return kind ? [{ file, kind }] : [];
		});
		if (media.length) {
			const { switchedTo, skipped } = addReferenceMedia(
				media,
				availableModels,
			);
			if (switchedTo) {
				toast.info(
					tI2V("switchedForMedia", {
						from: getModelDisplayName(model),
						to: getModelDisplayName(switchedTo),
					}),
				);
			}
			if (skipped) {
				toast.error(
					tI2V("maxMediaReached", {
						videos: referenceVideoMax ?? 0,
						audios: referenceAudioMax ?? 0,
					}),
				);
			}
		}
		const accepted = files
			.filter((file) => !getReferenceMediaKind(file))
			.map((file) => acceptFile(file))
			.filter((file): file is File => file !== null);
		if (accepted.length === 0) {
			return;
		}
		// 超额自己算：addImageFiles 的返回值在 setState updater 里赋值，React 延后执行时恒为 0
		addImageFiles(accepted);
		if (accepted.length > maxImages - filledImages.length) {
			toast.error(tI2V("maxImagesReached", { max: maxImages }));
		}
	};

	const availableModels = allowedModels
		? allowedModels.filter((id) => getI2VModel(id).modes.includes(mode))
		: getI2VModelsForMode(mode);
	const modelFamily = getI2VModelFamily(model);
	const menuModels = collapseI2VModelFamilies(availableModels);
	// 这页名单里有没有收参考视频 / 音频的档：有就让参考格也收（当前档不收会自动切过去）
	const fallbackLimits = <K extends "referenceVideo" | "referenceAudio">(
		key: K,
	) => {
		const id = availableModels.find((item) =>
			key === "referenceVideo"
				? supportsI2VReferenceVideo(item, mode)
				: supportsI2VReferenceAudio(item, mode),
		);
		return id ? getI2VModel(id)[key] : undefined;
	};
	const referenceVideoMax = (videoLimits ?? fallbackLimits("referenceVideo"))
		?.maxCount;
	const referenceAudioMax = (audioLimits ?? fallbackLimits("referenceAudio"))
		?.maxCount;
	const referenceAccept = [
		...ACCEPTED_IMAGE_TYPES,
		...(referenceVideoMax || referenceAudioMax
			? ACCEPTED_REFERENCE_MEDIA_TYPES
			: []),
	].join(",");
	// 加了视频 / 音频后不收那一类的档灰掉，返回灰掉的原因文案 key
	const blockedReason = (id: I2VModelId) =>
		referenceVideos.length && !supportsI2VReferenceVideo(id, mode)
			? "model.noVideoReference"
			: referenceAudios.length && !supportsI2VReferenceAudio(id, mode)
				? "model.noAudioReference"
				: null;
	const aspectRatioOptions = [...getI2VAspectRatios(model, mode)].sort(
		(a, b) => Number(b === "auto") - Number(a === "auto"),
	);
	const durationOptions =
		preset?.durationOptions ??
		getI2VDurationOptions(model, mode) ??
		Array.from(
			{
				length: modelConfig.duration.max - modelConfig.duration.min + 1,
			},
			(_, i) => modelConfig.duration.min + i,
		);
	const promptMax = getI2VPromptMaxLength(model, mode);
	// note 上限按每档最长的模板写法算、取最紧的那档，切模板、画质或填选填槽时不会突然超长
	const noteMax = preset
		? Math.min(
				...preset.qualities.map(
					(quality) =>
						getI2VPromptMaxLength(quality.model, preset.mode) -
						preset.notePrefixLength -
						Math.max(
							...preset.templates.flatMap((item) => [
								getTemplatePrompt(item, quality.id).length,
								getTemplatePrompt(item, quality.id, true)
									.length,
							]),
						),
				),
			)
		: 0;
	const canCreate =
		Boolean(prompt.trim()) &&
		prompt.length <= promptMax &&
		hasRequiredImages &&
		!isProcessingMedia &&
		!isPending;

	// 本次会话提交的优先；没有就用刷新后接手轮询的那一批，否则刷新后按钮在转、
	// 结果区却消失，成片出来也看不到
	const visibleBatchId = latestStartedBatchId ?? resumedBatchId;
	const batchItems = visibleBatchId
		? history.filter(
				(item): item is { type: "video" } & VideoHistoryItem =>
					item.type === "video" && item.batchId === visibleBatchId,
			)
		: [];
	// 上传一开始就出结果区，不用等提交成功拿到批次 id
	const showResults = isPending || Boolean(visibleBatchId);
	const allSettled =
		batchItems.length > 0 &&
		batchItems.every(
			(item) => item.status === "SUCCESS" || item.status === "FAILED",
		);
	// 再次提交时 latestStartedBatchId 还指着上一批（已完成），所以 isPending 且
	// 没有进行中的行就算「已提交、等任务」
	const phase: GenerationPhase = isUploading
		? "upload"
		: isPending && (batchItems.length === 0 || allSettled)
			? "submit"
			: allSettled
				? "done"
				: batchItems.length > 0
					? "generate"
					: "submit";
	const readyItem = batchItems.find((item) => item.status === "SUCCESS");
	// 出片用的是哪一档：行里存的是站内模型 id + 分辨率，和档位一一对应
	const readyQuality = readyItem
		? preset?.qualities.find(
				(item) =>
					item.model === readyItem.metadata?.modelId &&
					item.resolution === readyItem.resolution &&
					(item.duration === undefined ||
						item.duration === readyItem.duration),
			)
		: undefined;
	const upgradeTarget = readyQuality?.upgrade
		? preset?.qualities.find(
				(item) => item.id === readyQuality.upgrade?.targetId,
			)
		: undefined;
	// 步骤条描述的是这一批，刷新恢复时表单 mode 已回到默认，得看行里存的
	const batchMode = batchItems[0]?.metadata?.mode ?? mode;
	const generateAgainButton = (
		<Button
			type="button"
			variant="outline"
			onClick={() => {
				const item = batchItems[0];
				if (item) {
					applyHistoryItem(item);
				}
				setRegenerateRequested(true);
			}}
			disabled={isPending}
			className="h-10 gap-2 rounded-full border-black/10 px-4 font-medium text-gray-12 text-sm hover:bg-gray-3 [&>svg]:mr-0 [&>svg]:opacity-100"
		>
			<RefreshCwIcon className="size-4" />
			{t("results.generateAgain")}
		</Button>
	);
	// 同一批参数一致，拿一行回显：分辨率 / 比例 / 时长 / 模式
	const resultTags = (item: VideoHistoryItem): string[] => {
		const tags = [item.resolution.toUpperCase()];
		const ratio = item.metadata?.aspectRatio;
		if (ratio) {
			tags.push(
				ratio === "auto" ? tI2V("settings.autoAspectRatio") : ratio,
			);
		}
		tags.push(`${item.duration}s`);
		const itemMode = HERO_MODES.find(
			(heroMode) => heroMode === item.metadata?.mode,
		);
		// 文生 / 单图虽然合成一个 tab，结果标签仍分开写，一眼看出这条是哪种
		if (itemMode === "text") {
			tags.push(t("modeNames.text"));
		} else if (itemMode === "start") {
			tags.push(t("modeNames.image"));
		} else if (itemMode) {
			tags.push(t(`tabs.${itemMode}`));
		}
		return tags;
	};

	return (
		<section
			id={VIDEO_HERO_ID}
			className={cn(
				"relative overflow-hidden bg-gradient-to-b from-primary/10 via-background to-background px-4 pb-10 sm:px-6 lg:pb-16",
				// 模型页（modelPreset）10/28 起长在 Studio 外壳里，上面没有透明导航要让，顶部留白收小；
				// scroll-mt-14 让出手机顶栏的 56，滚回来 H1 不被盖住
				modelPreset
					? "scroll-mt-14 pt-6 sm:pt-12 md:scroll-mt-0 lg:pt-16"
					: "pt-[76px] sm:pt-32 lg:pt-[150px]",
			)}
		>
			{/* 标题区保持改版前的样子（实色大字 + 四色 pill），DeeVid 风格只用在下面的输入卡 */}
			<div className="mx-auto max-w-5xl text-center font-sans">
				<h1 className="font-bold text-3xl text-foreground leading-tight tracking-tight sm:text-6xl">
					{copy?.title ?? t("title")}
				</h1>
				<p className="mx-auto mt-2 max-w-3xl text-base text-muted-foreground sm:mt-4 sm:text-2xl">
					{copy?.subtitle ?? t("subtitle")}
				</p>

				<ul className="mt-3 flex flex-wrap justify-center gap-1.5 sm:mt-6 sm:gap-3">
					<Pill className="border-emerald-400/60 bg-emerald-50 text-emerald-600">
						{copy?.badges[0] ?? t("badges.free")}
					</Pill>
					<Pill className="border-amber-400/60 bg-amber-50 text-amber-800">
						{copy?.badges[1] ?? t("badges.modes")}
					</Pill>
					<Pill className="border-sky-400/60 bg-sky-50 text-sky-500">
						{copy?.badges[2] ?? t("badges.resolution")}
					</Pill>
					<Pill className="border-pink-400/60 bg-pink-50 text-pink-600">
						{copy?.badges[3] ?? t("badges.noCard")}
					</Pill>
				</ul>
				{/* 「15 free credits」只对没登录的人有意义；登录用户在首帧就藏（auth-hint.ts） */}
				<p className="mx-auto mt-2 max-w-3xl text-muted-foreground text-sm sm:mt-4 sm:text-base [[data-authed]_&]:hidden">
					{copy?.freeCredits ?? t("freeCredits")}
				</p>
			</div>

			{/* 输入卡（DeeVid composer，r28） */}
			<div
				ref={composerRef}
				className="relative mx-auto mt-5 max-w-[960px] scroll-mt-20 sm:mt-10"
			>
				<div className="relative z-10 flex min-h-[196px] flex-col gap-3 rounded-28 sm:gap-5 bg-white p-4 text-left font-sans shadow-card sm:px-6 sm:py-5">
					{preset ? (
						/* 预设页：模板 chip 顶替模式 tab，样式同 TabsTrigger；只有一条模板就不出 chip */
						preset.templates.length > 1 ? (
							<fieldset
								aria-label={preset.copy.templatesLabel}
								className="flex flex-nowrap gap-1.5 overflow-x-auto sm:flex-wrap sm:gap-2"
							>
								{preset.templates.map((item) => (
									<button
										key={item.id}
										type="button"
										onClick={() =>
											setPrompt(
												preset.buildPrompt(
													getTemplatePrompt(
														item,
														activeQuality?.id,
														optionalEmpty,
													),
													note,
												),
											)
										}
										data-state={
											item.id === activeTemplate?.id
												? "active"
												: "inactive"
										}
										className="h-11 whitespace-nowrap rounded-full bg-gray-4 px-3.5 font-medium font-ui text-[13px] text-gray-9 transition-colors hover:text-gray-12 data-[state=active]:bg-brand-a08 data-[state=active]:text-brand sm:h-9 sm:text-xs"
									>
										{item.label}
									</button>
								))}
							</fieldset>
						) : null
					) : (
						<Tabs
							value={tabOf(mode)}
							onValueChange={(value) =>
								value !== tabOf(mode) &&
								setMode(
									value === "start"
										? "text"
										: (value as HeroMode),
								)
							}
						>
							<TabsList
								aria-label={t("tabsLabel")}
								className="flex flex-wrap justify-start gap-1.5 border-0 sm:gap-2"
							>
								{visibleTabs.map((item) => {
									const Icon = HERO_TAB_ICONS[item];
									return (
										<TabsTrigger
											key={item}
											value={item}
											className="mb-0 h-9 gap-1.5 whitespace-nowrap rounded-full border-0 bg-gray-4 px-2 font-medium font-ui text-[11px] text-gray-9 hover:text-gray-12 min-[390px]:text-xs data-[state=active]:bg-brand-a08 data-[state=active]:text-brand sm:px-3.5"
										>
											<Icon className="hidden size-3.5 sm:block" />
											{t(`tabs.${item}`)}
										</TabsTrigger>
									);
								})}
							</TabsList>
						</Tabs>
					)}

					<div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
						{mode === "reference" && !preset ? (
							/* 参考图 / 视频 / 音频：追加列表，满了不再出加号格；桌面端限宽两列，别把提示词框挤没。
							   一个都没传时是整条宽格，写清楚收哪几类（老公 10/10 定） */
							// 手机上「0 / 9」挪到格子右边同一行，省一行高度
							<div className="flex shrink-0 items-end gap-3 sm:w-[16.5rem] sm:flex-col sm:items-start sm:gap-2">
								{/* self-stretch：外层竖排是 items-start，不撑开的话空态宽格只有内容那么宽 */}
								<div className="flex flex-1 flex-wrap gap-3 sm:self-stretch">
									{filledImages.map((image, index) => (
										<UploadBox
											key={image.previewUrl}
											previewUrl={image.previewUrl}
											label={t("referenceUpload")}
											removeLabel={t("remove")}
											compact
											onFiles={() => {}}
											onRemove={() => removeImage(index)}
										/>
									))}
									{referenceMedia.map((item) => (
										<ReferenceMediaThumb
											key={item.id}
											media={item}
											processingLabel={tI2V(
												"slot.mediaProcessing",
												{
													percent: Math.round(
														item.progress * 100,
													),
												},
											)}
											removeLabel={t("remove")}
											onRemove={() =>
												removeReferenceMedia(item.id)
											}
											className="size-20 rounded-2xl"
										/>
									))}
									{filledImages.length === 0 &&
									referenceMedia.length === 0 ? (
										<UploadBox
											previewUrl={null}
											label={
												referenceVideoMax ||
												referenceAudioMax
													? t("referenceUploadAll")
													: t("referenceUpload")
											}
											hint={t("referenceUploadHint")}
											removeLabel={t("remove")}
											banner
											multiple
											accept={referenceAccept}
											onFiles={handleReferenceFiles}
											onRemove={() => {}}
										/>
									) : filledImages.length < maxImages ||
										(referenceVideoMax &&
											referenceVideos.length <
												referenceVideoMax) ||
										(referenceAudioMax &&
											referenceAudios.length <
												referenceAudioMax) ? (
										<UploadBox
											previewUrl={null}
											label={t("referenceAddMore")}
											removeLabel={t("remove")}
											compact
											iconOnly
											multiple
											accept={referenceAccept}
											onFiles={handleReferenceFiles}
											onRemove={() => {}}
										/>
									) : null}
								</div>
								{/* 视频 / 音频传了才显示，0 / 3 不占位；每段不拆行，要折只在 · 处折（10/22） */}
								<span className="text-ink-muted text-xs tabular-nums">
									<span className="whitespace-nowrap">
										{tI2V("slot.counter", {
											count: filledImages.length,
											max: maxImages,
										})}
									</span>
									{referenceVideoMax &&
									referenceVideos.length ? (
										<span className="whitespace-nowrap">
											{` · ${tI2V("slot.videoCounter", {
												count: referenceVideos.length,
												max: referenceVideoMax,
											})}`}
										</span>
									) : null}
									{referenceAudioMax &&
									referenceAudios.length ? (
										<span className="whitespace-nowrap">
											{` · ${tI2V("slot.audioCounter", {
												count: referenceAudios.length,
												max: referenceAudioMax,
											})}`}
										</span>
									) : null}
									{referenceVideos.length ? (
										<span className="block">
											{tI2V("slot.videoBilling")}
										</span>
									) : null}
								</span>
							</div>
						) : mode === "text" ? (
							/* 合并 tab 的文生状态：上传框可选，传了图就切到单图。
							   手机上挪到提示词下面、改整宽矮条，先让人看到能直接打字 */
							<div className="order-last flex shrink-0 gap-3 sm:order-none">
								<UploadBox
									previewUrl={null}
									label={
										uploadNudge
											? t("uploadForTemplate")
											: t("upload")
									}
									removeLabel={t("remove")}
									highlight={uploadNudge}
									wide
									onFiles={(files) => {
										setUploadNudge(false);
										handleTextModeFile(files[0]);
									}}
									onRemove={() => {}}
								/>
							</div>
						) : (
							<div className="flex shrink-0 gap-3">
								{inputImages.map((image, index) => {
									// 默认图要等上面的 effect 才写进 state，SSR 和首帧先按默认图画，
									// 不然第二槽会先闪一帧上传框
									const previewUrl =
										image?.previewUrl ??
										(mode === preset?.mode
											? slotDefaults?.[index]
											: null) ??
										null;
									const isDefault = Boolean(
										previewUrl &&
											previewUrl ===
												slotDefaults?.[index],
									);
									return (
										<UploadBox
											key={index}
											previewUrl={previewUrl}
											swapLabel={
												isDefault &&
												!preset?.lockSlotDefaults
													? preset?.copy.swapLabel
													: undefined
											}
											locked={
												isDefault &&
												Boolean(
													preset?.lockSlotDefaults,
												)
											}
											label={
												preset
													? preset.slotLabels[index]
													: mode === "between"
														? t(
																index === 0
																	? "startFrame"
																	: "endFrame",
															)
														: t("upload")
											}
											removeLabel={t("remove")}
											fill={inputImages.length > 1}
											short={Boolean(preset)}
											onFiles={(files) =>
												handleSlotFile(index, files[0])
											}
											onRemove={() =>
												// 合并 tab 删掉唯一那张图 = 回到文生，prompt 保留
												mode === "start"
													? setMode("text")
													: setImageAt(index, null)
											}
										/>
									);
								})}
							</div>
						)}
						{preset && activeTemplate ? (
							/* 预设页：模板不给改，只给一句可选的 director's note，拼到模板后面 */
							<div className="flex min-w-0 flex-1 flex-col gap-2">
								<label
									htmlFor={VIDEO_HERO_PROMPT_ID}
									className="font-medium text-gray-9 text-xs"
								>
									{preset.copy.noteLabel}
								</label>
								<textarea
									id={VIDEO_HERO_PROMPT_ID}
									value={note}
									maxLength={noteMax}
									onChange={(event) =>
										setPrompt(
											preset.buildPrompt(
												activeTemplatePrompt ??
													activeTemplate.prompt,
												event.target.value,
											),
										)
									}
									placeholder={preset.copy.notePlaceholder}
									rows={3}
									className="min-h-20 w-full flex-1 resize-none rounded-2xl bg-gray-2 px-4 py-3 text-base text-gray-12 outline-none ring-1 ring-black/[.04] placeholder:text-ink-muted focus:ring-brand/40"
								/>
								<span
									className={cn(
										"self-end text-ink-muted text-xs tabular-nums",
										// 同主提示词框：手机上空着不占行
										note.length === 0 && "max-sm:hidden",
									)}
								>
									{note.length} / {noteMax}
								</span>
							</div>
						) : (
							<div className="flex min-w-0 flex-1 flex-col">
								<textarea
									id={VIDEO_HERO_PROMPT_ID}
									value={prompt}
									onChange={(event) =>
										setPrompt(event.target.value)
									}
									placeholder={
										mode === "text"
											? t("promptPlaceholderText")
											: tI2V(
													mode === "between"
														? "prompt.placeholderBetween"
														: mode === "reference"
															? referenceMedia.length
																? "prompt.placeholderReferenceVideo"
																: "prompt.placeholderReference"
															: "prompt.placeholderStart",
												)
									}
									aria-label={tI2V("prompt.label")}
									rows={3}
									className="min-h-20 w-full flex-1 resize-none bg-transparent text-base text-gray-12 outline-none placeholder:text-ink-muted sm:min-h-28"
								/>
								<span
									className={cn(
										"self-end text-xs tabular-nums",
										// 手机上空着时不占一行，开始打字再出
										prompt.length === 0 && "max-sm:hidden",
										prompt.length > promptMax
											? "text-error"
											: "text-ink-muted",
									)}
								>
									{prompt.length} / {promptMax}
								</span>
							</div>
						)}
					</div>

					{/* 手机上参数胶囊正常折行（2026-10-05 老公嫌横滑截半个胶囊），Create 独占一行满宽：
					    主操作要像主操作，不跟胶囊挤一行；首屏放不下也不再为它压别的 */}
					<div className="flex flex-wrap items-center gap-x-1.5 gap-y-2 sm:flex-nowrap sm:justify-between sm:gap-3">
						{preset ? (
							/* 预设页：画质 / 比例 / 时长三组分段，模型和分辨率藏在画质档里 */
							// 手机上 contents：三组分段和 Create 同一个折行容器，不再横滑截半个按钮
							<div className="contents sm:flex sm:flex-wrap sm:gap-2">
								<Segmented label={preset.copy.qualityLabel}>
									{preset.qualities.map((item) => (
										<SegmentButton
											key={item.id}
											active={
												item.id === activeQuality?.id
											}
											onClick={() => selectQuality(item)}
											title={item.hint}
										>
											{item.label}
											{lockedResolutions.has(
												item.resolution,
											) ? (
												<CrownIcon className="size-3 text-warning" />
											) : null}
											<span className="font-normal text-gray-9 text-xs tabular-nums sm:text-[10px]">
												{/* 手机上只留积分数，Lite/Standard/HD 本身就是档位，三档才排得进一行 */}
												<span className="max-sm:hidden">
													{`${item.resolution} · `}
												</span>
												{getI2VCreditCost(
													item.model,
													item.resolution,
													// 没固定时长的档按切过去后的实际时长标价：当前时长不在 durationOptions 里
													//（比如 Lite 固定 4 秒）时，切档会被同步 effect 改回 defaultDuration
													item.duration ??
														(preset.durationOptions.includes(
															duration,
														)
															? duration
															: preset.defaultDuration),
													// 模板带固定参考视频时，收视频的档按这一档那段的秒数加价
													(() => {
														const video =
															getTemplateVideo(
																activeTemplate,
																item.id,
																optionalEmpty,
															);
														return video &&
															supportsI2VReferenceVideo(
																item.model,
																preset.mode,
															)
															? getBillableVideoSeconds(
																	video.durationSec,
																)
															: 0;
													})(),
												)}
											</span>
										</SegmentButton>
									))}
								</Segmented>
								{activeQuality?.note ? (
									// 手机上紧跟画质分段独占一行，用户选档时就看得到
									<p className="basis-full text-gray-10 text-xs leading-snug sm:hidden">
										{activeQuality.note}
									</p>
								) : null}
								<Segmented label={preset.copy.ratioLabel}>
									{/* 第二槽选填的页（AI Kiss）只传一张图时，Grok 不收比例、成片跟原图走：
									    只显示一个 Auto，别让人以为选了 9:16 */}
									{preset.requiredSlots !== undefined &&
									!canPickAspectRatio &&
									hasRequiredImages ? (
										<SegmentButton
											active
											onClick={() => {}}
										>
											{tI2V("settings.autoAspectRatio")}
										</SegmentButton>
									) : null}
									{(preset.requiredSlots !== undefined &&
									!canPickAspectRatio &&
									hasRequiredImages
										? []
										: lockedRatio
											? [lockedRatio]
											: preset.aspectRatioOptions
									).map((ratio) => (
										<SegmentButton
											key={ratio}
											active={
												ratio ===
												(lockedRatio ?? presetRatio)
											}
											onClick={() =>
												!lockedRatio &&
												selectRatio(ratio)
											}
										>
											<AspectRatioSwatch
												value={ratio}
												size={14}
												className="border border-current bg-transparent"
											/>
											{ratio}
										</SegmentButton>
									))}
								</Segmented>
								<Segmented label={preset.copy.durationLabel}>
									{presetDurations.map((item) => (
										<SegmentButton
											key={item}
											active={item === duration}
											onClick={() => setDuration(item)}
										>
											{`${item}s`}
										</SegmentButton>
									))}
								</Segmented>
							</div>
						) : (
							// 手机上 contents：胶囊直接当外层折行容器的子项，和 Create 一起排
							<div className="contents sm:flex sm:flex-wrap sm:gap-2">
								{/* 模型 */}
								<DropdownMenu>
									<Chip
										label={
											modelFamily?.name ??
											getModelDisplayName(model)
										}
									/>
									<DropdownMenuContent
										align="start"
										className="w-72 p-2"
									>
										{menuModels.map((id) => {
											const config = getI2VModel(id);
											const hint = getI2VModelPriceHint(
												id,
												referenceVideoSeconds,
											);
											const family =
												getI2VModelFamily(id);
											// 多档族这一条代表整族，已在族里时选它不换档
											const selected = family
												? family === modelFamily
												: id === model;
											const blocked = blockedReason(
												id as I2VModelId,
											);
											return (
												<DropdownMenuItem
													key={id}
													disabled={!!blocked}
													onSelect={() => {
														if (!selected) {
															setModel(
																id as I2VModelId,
															);
														}
													}}
													className={cn(
														"flex cursor-pointer items-start justify-between gap-3 rounded-md px-2 py-2",
														selected &&
															"bg-muted/60",
													)}
												>
													<span className="min-w-0">
														<span className="block font-medium text-sm">
															{family?.name ??
																getModelDisplayName(
																	id as I2VModelId,
																)}
														</span>
														<span className="block text-muted-foreground text-xs">
															{blocked
																? tI2V(blocked)
																: tI2V(
																		`model.tier${config.tier}`,
																	)}
														</span>
													</span>
													<span className="flex shrink-0 flex-col items-end text-muted-foreground text-xs">
														<span className="flex items-center gap-1">
															<ZapIcon className="size-3" />
															{hint.credits}
														</span>
														<span className="text-[10px]">
															{`${hint.resolution.toUpperCase()} · ${hint.duration}s`}
														</span>
													</span>
												</DropdownMenuItem>
											);
										})}
									</DropdownMenuContent>
								</DropdownMenu>

								{/* 多档模型（Veo 3.1）的档位，照 imgveo；积分按当前分辨率和时长算 */}
								{modelFamily ? (
									<DropdownMenu>
										<Chip
											label={
												modelFamily.variants.find(
													(variant) =>
														variant.id === model,
												)?.name ?? ""
											}
										/>
										<DropdownMenuContent
											align="start"
											className="min-w-36 p-1"
										>
											{modelFamily.variants
												.filter((variant) =>
													availableModels.includes(
														variant.id,
													),
												)
												.map((variant) => (
													<DropdownMenuItem
														key={variant.id}
														disabled={
															!!blockedReason(
																variant.id,
															)
														}
														onSelect={() =>
															setModel(variant.id)
														}
														className={cn(
															"flex cursor-pointer items-center justify-between gap-3",
															variant.id ===
																model &&
																"bg-muted/60",
														)}
													>
														{variant.name}
														<span className="flex items-center gap-1 text-muted-foreground text-xs">
															<ZapIcon className="size-3" />
															{getI2VCreditCost(
																variant.id,
																resolution,
																duration,
																referenceVideoSeconds,
															)}
														</span>
													</DropdownMenuItem>
												))}
										</DropdownMenuContent>
									</DropdownMenu>
								) : null}

								{/* 分辨率：锁定档点了走付费墙 */}
								<DropdownMenu>
									<Chip label={resolution.toUpperCase()} />
									<DropdownMenuContent
										align="start"
										className="min-w-32 p-1"
									>
										{modelConfig.resolutions.map((item) => {
											const locked =
												lockedResolutions.has(item);
											return (
												<DropdownMenuItem
													key={item}
													onSelect={() =>
														locked
															? setPaywallReason(
																	"video-resolution",
																)
															: setResolution(
																	item as I2VResolution,
																)
													}
													className={cn(
														"flex cursor-pointer items-center justify-between gap-2",
														item === resolution &&
															"bg-muted/60",
													)}
												>
													{item.toUpperCase()}
													{locked ? (
														<CrownIcon className="size-3 text-warning" />
													) : null}
												</DropdownMenuItem>
											);
										})}
									</DropdownMenuContent>
								</DropdownMenu>

								{/* 比例：只有模型允许挑的时候才出现 */}
								{canPickAspectRatio &&
								aspectRatioOptions.length > 1 ? (
									<DropdownMenu>
										<Chip
											label={
												aspectRatio === "auto"
													? tI2V(
															"settings.autoAspectRatio",
														)
													: aspectRatio
											}
										/>
										<DropdownMenuContent
											align="start"
											className="min-w-28 p-1"
										>
											{aspectRatioOptions.map((ratio) => (
												<DropdownMenuItem
													key={ratio}
													onSelect={() =>
														setAspectRatio(
															ratio as I2VAspectRatio,
														)
													}
													className={cn(
														"cursor-pointer",
														ratio === aspectRatio &&
															"bg-muted/60",
													)}
												>
													{ratio === "auto"
														? tI2V(
																"settings.autoAspectRatio",
															)
														: ratio}
												</DropdownMenuItem>
											))}
										</DropdownMenuContent>
									</DropdownMenu>
								) : null}

								{/* 时长 */}
								<DropdownMenu>
									<Chip label={`${duration}s`} />
									<DropdownMenuContent
										align="start"
										className="max-h-64 min-w-24 overflow-y-auto p-1"
									>
										{durationOptions.map((item) => (
											<DropdownMenuItem
												key={item}
												onSelect={() =>
													setDuration(item)
												}
												className={cn(
													"cursor-pointer tabular-nums",
													item === duration &&
														"bg-muted/60",
												)}
											>
												{item}s
											</DropdownMenuItem>
										))}
									</DropdownMenuContent>
								</DropdownMenu>
								{modelPreset?.showAudioToggle &&
								model === "seedance-2.5" ? (
									<button
										type="button"
										role="switch"
										aria-label="Audio"
										aria-checked={generateAudio}
										onClick={() =>
											setGenerateAudio(!generateAudio)
										}
										className="flex h-9 shrink-0 items-center gap-2 rounded-full bg-gray-4 px-3 font-ui text-gray-11 text-xs sm:px-3.5"
									>
										Audio
										<span
											className={cn(
												"flex h-4 w-7 items-center rounded-full p-0.5 transition-colors",
												generateAudio
													? "bg-brand"
													: "bg-gray-7",
											)}
										>
											<span
												className={cn(
													"size-3 rounded-full bg-white transition-transform",
													generateAudio &&
														"translate-x-3",
												)}
											/>
										</span>
									</button>
								) : null}
							</div>
						)}

						<button
							type="button"
							onClick={startGeneration}
							disabled={!canCreate}
							className="flex h-12 basis-full items-center justify-center gap-3 rounded-full bg-gray-12 px-8 font-medium text-base text-white transition-colors hover:bg-gray-11 disabled:bg-white disabled:text-gray-12/45 disabled:ring-1 disabled:ring-black/[.08] max-sm:mt-1 sm:basis-auto"
						>
							{isPending ? (
								<>
									<Loader2Icon className="size-4 animate-spin" />
									{isUploading
										? tI2V("uploading")
										: tI2V("generating")}
								</>
							) : (
								<>
									{tI2V("create")}
									<span className="flex items-center gap-1 border-current/30 border-l pl-3">
										<ZapIcon className="size-4 fill-current" />
										{creditCost}
									</span>
								</>
							)}
						</button>
					</div>
					{activeQuality?.note ? (
						/* 桌面上分段和 Create 同一行，说明放到整行下面；手机上的那份在画质分段后面 */
						<p className="-mt-2 hidden text-gray-10 text-xs sm:block">
							{activeQuality.note}
						</p>
					) : null}
				</div>
				{/* 卡片下沿的彩色光晕（DESIGN.md §7.6.2） */}
				<div
					aria-hidden="true"
					className="-bottom-6 pointer-events-none absolute inset-x-[8%] flex h-16 gap-6 opacity-80 blur-[50px]"
				>
					<span className="flex-1 rounded-full bg-[rgba(0,75,255,0.22)]" />
					<span className="flex-1 rounded-full bg-[rgba(161,74,255,0.2)]" />
					<span className="flex-1 rounded-full bg-[rgba(0,190,255,0.22)]" />
				</div>
			</div>
			{/* 手机上不出：和上面四个徽章说的是同一件事，三行竖排白占首屏 */}
			<ul className="mx-auto mt-8 hidden max-w-[960px] text-ink-3 text-sm sm:flex sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-x-8 sm:gap-y-2">
				{(
					copy?.trust ??
					(["aiContent", "commercial", "resolution"] as const).map(
						(key) => t(`trust.${key}`),
					)
				).map((text) => (
					<li key={text} className="flex items-center gap-2">
						<span className="size-1.5 rounded-full bg-brand" />
						{text}
					</li>
				))}
			</ul>

			{showResults ? (
				<div
					ref={resultsRef}
					className="mx-auto mt-10 max-w-[960px] scroll-mt-20"
				>
					<div className="mb-3 flex items-center justify-between">
						<h2 className="font-semibold text-gray-12 text-lg">
							{t("results.title")}
						</h2>
						<LocaleLink
							href="/image-to-video"
							className="text-brand text-sm hover:underline"
						>
							{t("results.viewAll")}
						</LocaleLink>
					</div>
					<div className="rounded-20 bg-white p-4 text-left shadow-card sm:p-6">
						{phase === "done" ? (
							readyItem ? (
								<div className="mb-4">
									<p className="flex items-center gap-2 font-semibold text-foreground text-lg">
										<CircleCheckIcon className="size-5 text-success" />
										{t("results.ready")}
									</p>
									<p className="mt-1 line-clamp-1 text-muted-foreground text-sm">
										{readyItem.prompt}
									</p>
									<ul className="mt-3 flex flex-wrap gap-2">
										{resultTags(readyItem).map((tag) => (
											<li
												key={tag}
												className="rounded-md bg-muted px-2.5 py-1 text-muted-foreground text-xs"
											>
												{tag}
											</li>
										))}
									</ul>
								</div>
							) : null
						) : (
							<GenerationSteps
								phase={phase}
								showUpload={batchMode !== "text"}
								labels={{
									upload: t("results.steps.upload"),
									submit: t("results.steps.submit"),
									generate: t("results.steps.generate"),
								}}
							/>
						)}

						{phase === "upload" || phase === "submit" ? (
							<ResultPlaceholder>
								<Loader2Icon className="size-6 animate-spin" />
								{phase === "upload"
									? t("results.uploading")
									: t("results.submitting")}
							</ResultPlaceholder>
						) : (
							<div
								className={cn(
									"grid gap-4",
									batchItems.length > 1 && "sm:grid-cols-2",
								)}
							>
								{batchItems.map((item) =>
									item.status === "SUCCESS" &&
									item.videoUrl ? (
										<div key={item.id}>
											{/* 竖版成片按 9:16 显示，不然 16:9 框里两边全是黑边 */}
											<video
												src={item.videoUrl}
												controls
												playsInline
												className={cn(
													"w-full rounded-xl bg-black object-contain",
													item.metadata
														?.aspectRatio === "9:16"
														? "mx-auto aspect-[9/16] max-h-[640px]"
														: "aspect-video",
												)}
											>
												<track kind="captions" />
											</video>
											<div className="mt-3 flex flex-wrap items-center gap-3">
												{/* 用 button 而不是 <a>：nextjs-toploader 会接管同源 <a> 的点击，
												    下载响应不换页，顶部进度条就一直挂着 */}
												<button
													type="button"
													onClick={() => {
														window.location.href = `/api/proxy-download?url=${encodeURIComponent(item.videoUrl ?? "")}`;
													}}
													className="inline-flex h-10 items-center gap-2 rounded-full bg-gray-12 px-4 font-medium text-sm text-white hover:bg-gray-11"
												>
													<DownloadIcon className="size-4" />
													{t("results.download")}
												</button>
												{/* 单条输出时和 Download 同一行；多条时放到网格下面只出一个 */}
												{phase === "done" &&
												batchItems.length === 1
													? generateAgainButton
													: null}
											</div>
										</div>
									) : item.status === "FAILED" ? (
										<ResultPlaceholder key={item.id}>
											{t("results.failed")}
											<span className="text-xs">
												{t("results.failedNoCharge")}
											</span>
										</ResultPlaceholder>
									) : (
										<ResultPlaceholder key={item.id}>
											<Loader2Icon className="size-6 animate-spin" />
											{t("results.generating")}
										</ResultPlaceholder>
									),
								)}
							</div>
						)}

						{phase === "done" ? (
							batchItems.length > 1 ? (
								<div className="mt-4">
									{generateAgainButton}
								</div>
							) : null
						) : (
							<p className="mt-4 flex items-center justify-center gap-1.5 text-muted-foreground text-xs">
								<ZapIcon className="size-3.5" />
								{t("results.chargeNote")}
							</p>
						)}

						{phase === "done" &&
						readyQuality?.upgrade &&
						upgradeTarget ? (
							<div className="mt-5 flex flex-col gap-3 rounded-xl bg-brand/5 p-4 sm:flex-row sm:items-center sm:justify-between">
								<p className="text-foreground text-sm">
									{readyQuality.upgrade.text}
								</p>
								<Button
									type="button"
									className="shrink-0"
									onClick={() => {
										void trackEvent(
											"tier-upgrade-clicked",
											{
												from: readyQuality.id,
												to: upgradeTarget.id,
											},
										);
										selectQuality(upgradeTarget);
										composerRef.current?.scrollIntoView({
											behavior: "smooth",
											block: "start",
										});
									}}
								>
									{readyQuality.upgrade.cta}
								</Button>
							</div>
						) : null}

						{preset && phase === "done" && readyItem ? (
							/* 热梗预设页出完片推另外几个热梗，把「用完即走」的会话接下去（哥飞 page_coach P2） */
							<div className="mt-6 border-gray-3 border-t pt-5">
								<p className="mb-3 font-semibold text-foreground text-sm">
									{preset.copy.moreLinks ??
										t("results.moreTrends")}
								</p>
								<VideoTrendCards placement="result" />
							</div>
						) : null}
					</div>
				</div>
			) : null}

			<CreditsPaywallModal
				open={paywallReason !== null}
				onOpenChange={(open) => !open && setPaywallReason(null)}
				reason={paywallReason ?? "insufficient-credits"}
			/>
		</section>
	);
}

function Pill({
	className,
	children,
}: { className: string; children: ReactNode }) {
	return (
		<li
			className={cn(
				"rounded-full border px-3 py-1 font-medium text-xs sm:px-4 sm:py-1.5 sm:text-base",
				className,
			)}
		>
			{children}
		</li>
	);
}

/** 预设页的分段控件：灰底胶囊里一排按钮，选中的那个浮白 */
function Segmented({
	label,
	children,
}: { label: string; children: ReactNode }) {
	return (
		<fieldset
			aria-label={label}
			className="flex h-11 shrink-0 items-center gap-0.5 rounded-full bg-gray-4 p-[3px] sm:h-9"
		>
			{children}
		</fieldset>
	);
}

function SegmentButton({
	active,
	onClick,
	title,
	children,
}: {
	active: boolean;
	onClick: () => void;
	title?: string;
	children: ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			title={title}
			aria-pressed={active}
			className={cn(
				// 手机上胶囊 44 高、按钮扣掉 3px 内边距只剩 38：after 往上下各撑 3px 把点击区铺满，
				// 窄的（5s）补到 44 宽，样子不变
				"relative flex h-full min-w-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 font-medium font-ui text-xs transition-colors after:absolute after:inset-x-0 after:-inset-y-[3px] sm:min-w-0",
				active
					? "bg-white text-gray-12 shadow-sm"
					: "text-gray-9 hover:text-gray-12",
			)}
		>
			{children}
		</button>
	);
}

/** 竞品同款三步进度条；文生视频没有上传，只剩后两步 */
function GenerationSteps({
	phase,
	showUpload,
	labels,
}: {
	phase: Exclude<GenerationPhase, "done">;
	showUpload: boolean;
	labels: Record<Exclude<GenerationPhase, "done">, string>;
}) {
	const steps = (["upload", "submit", "generate"] as const).filter(
		(step) => showUpload || step !== "upload",
	);
	// 文生视频也会走一瞬间 upload（空数组），落到第一步
	const currentIndex = Math.max(0, steps.indexOf(phase));
	return (
		<ol className="mb-4 flex items-center justify-center gap-2 sm:gap-3">
			{steps.map((step, index) => {
				const done = index < currentIndex;
				const current = index === currentIndex;
				return (
					<li key={step} className="flex items-center gap-2 sm:gap-3">
						{index > 0 ? (
							<span
								className={cn(
									"h-px w-6 sm:w-12",
									done || current
										? "bg-primary"
										: "bg-border",
								)}
							/>
						) : null}
						<span
							className={cn(
								"flex size-8 items-center justify-center rounded-full border",
								done &&
									"border-primary bg-primary text-primary-foreground",
								current && "border-primary text-primary",
								!done &&
									!current &&
									"border-border text-muted-foreground",
							)}
						>
							{done ? (
								<CheckIcon className="size-4" />
							) : current ? (
								<Loader2Icon className="size-4 animate-spin" />
							) : (
								<span className="text-xs">{index + 1}</span>
							)}
						</span>
						<span
							className={cn(
								"font-medium text-sm",
								done
									? "text-primary"
									: current
										? "text-foreground"
										: "text-muted-foreground",
							)}
						>
							{labels[step]}
						</span>
					</li>
				);
			})}
		</ol>
	);
}
